import { useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  Plus,
} from "lucide-react";
import { useCreate, useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { ContentPiece, ContentPieceVersion, PieceFormat } from "../types";
import { CONTENT_BUCKET, MAX_UPLOAD_BYTES } from "./pieceModel";
import { getLinkEmbed } from "./linkEmbed";
import { useSignedUrl } from "./useSignedUrl";

const isImagePath = (path: string) =>
  /\.(png|jpe?g|webp|gif|avif)$/i.test(path);
const isVideoPath = (path: string) => /\.(mp4|mov|m4v|webm)$/i.test(path);

const versionFiles = (version: ContentPieceVersion) =>
  [version.file_path, ...(version.extra_file_paths ?? [])].filter(
    (path): path is string => Boolean(path),
  );

// Frame proportions as each format is published on social networks.
const FRAME_BY_FORMAT: Record<PieceFormat, string> = {
  reel: "aspect-[9/16] max-h-[78vh]",
  story: "aspect-[9/16] max-h-[78vh]",
  post: "aspect-[4/5]",
  carousel: "aspect-[4/5]",
  video: "aspect-video",
  other: "aspect-[4/5]",
};

export const PieceVersions = ({
  piece,
  versions,
  accountName,
  canAdd = true,
}: {
  piece: ContentPiece;
  versions: ContentPieceVersion[];
  accountName?: string;
  canAdd?: boolean;
}) => {
  const translate = useTranslate();
  const [isAdding, setIsAdding] = useState(false);
  const [latest, ...older] = versions;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          {translate("resources.content_pieces.versions.title")}
        </span>
        {canAdd && !isAdding ? (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-1 text-xs font-semibold text-primary"
          >
            <Plus className="size-3.5" />
            {translate("resources.content_pieces.action.add_version")}
          </button>
        ) : null}
      </div>

      {isAdding ? (
        <AddVersionForm
          piece={piece}
          nextNumber={(latest?.version_number ?? 0) + 1}
          onDone={() => setIsAdding(false)}
        />
      ) : null}

      {latest ? (
        <PostPreview piece={piece} version={latest} accountName={accountName} />
      ) : (
        <div className="grid h-32 place-items-center rounded-2xl bg-secondary text-sm text-muted-foreground">
          {translate("resources.content_pieces.versions.empty")}
        </div>
      )}

      {older.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {older.map((version) => (
            <li key={version.id}>
              <VersionRow version={version} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

// The latest version shown as it would look once published: account header,
// the media in its real proportions (swipeable when there are several files)
// and the caption underneath.
const PostPreview = ({
  piece,
  version,
  accountName,
}: {
  piece: ContentPiece;
  version: ContentPieceVersion;
  accountName?: string;
}) => {
  const translate = useTranslate();
  const files = versionFiles(version);
  const label = translate("resources.content_pieces.versions.version", {
    number: version.version_number,
  });

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-background">
      <div className="flex items-center gap-2.5 px-3.5 py-2.5">
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-secondary text-xs font-semibold uppercase">
          {(accountName ?? "?").trim().charAt(0)}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">
          {accountName}
        </span>
        <span className="shrink-0 rounded-full bg-secondary px-2.5 py-0.5 text-[11px] text-muted-foreground">
          {label}
        </span>
      </div>

      {files.length > 0 ? (
        <MediaCarousel files={files} format={piece.format} />
      ) : null}

      {version.external_url ? (
        <LinkMedia url={version.external_url} format={piece.format} />
      ) : null}

      <div className="flex flex-col gap-2 px-3.5 pt-3 pb-3.5">
        {piece.caption ? (
          <Caption text={piece.caption} name={accountName} />
        ) : null}
        {version.note ? (
          <p className="text-xs text-muted-foreground">{version.note}</p>
        ) : null}
      </div>
    </div>
  );
};

// A Drive, YouTube or Canva link plays inside the preview; any other link
// stays as a button. The original link is always reachable underneath.
const LinkMedia = ({ url, format }: { url: string; format: PieceFormat }) => {
  const translate = useTranslate();
  const embed = getLinkEmbed(url);
  const isFolder = embed?.src.includes("embeddedfolderview");
  const isShort = url.includes("/shorts/");
  const frame = isFolder
    ? "h-[420px]"
    : embed?.provider === "youtube"
      ? isShort
        ? FRAME_BY_FORMAT.reel
        : FRAME_BY_FORMAT.video
      : FRAME_BY_FORMAT[format];

  return (
    <div className="flex flex-col">
      {embed ? (
        <div className={`mx-auto w-full bg-black ${frame}`}>
          <iframe
            src={embed.src}
            title={translate("resources.content_pieces.versions.open_link")}
            className="h-full w-full border-0"
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      ) : null}
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="mx-3.5 mt-3 flex items-center gap-2 rounded-xl bg-secondary px-3 py-2.5 text-sm no-underline hover:bg-accent"
      >
        <ExternalLink className="size-4 shrink-0 text-primary" />
        <span className="min-w-0 flex-1 truncate">
          {translate(
            embed
              ? `resources.content_pieces.versions.open_in_${embed.provider}`
              : "resources.content_pieces.versions.open_link",
          )}
        </span>
      </a>
      {embed?.provider === "drive" || embed?.provider === "canva" ? (
        <p className="mx-3.5 mt-1.5 text-[11px] text-muted-foreground">
          {translate("resources.content_pieces.versions.share_hint")}
        </p>
      ) : null}
    </div>
  );
};

const MediaCarousel = ({
  files,
  format,
}: {
  files: string[];
  format: PieceFormat;
}) => {
  const translate = useTranslate();
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  const goTo = (next: number) => {
    const track = trackRef.current;
    if (!track) return;
    const left = next * track.clientWidth;
    track.scrollTo({ left, behavior: "smooth" });
    // Some browsers skip smooth scrolling (reduced motion, background tabs):
    // make sure the slide still changes.
    window.setTimeout(() => {
      if (Math.abs(track.scrollLeft - left) > 2) {
        track.scrollTo({ left, behavior: "instant" });
      }
    }, 450);
  };

  return (
    <div className="relative bg-black">
      <div
        ref={trackRef}
        onScroll={(event) => {
          const track = event.currentTarget;
          setIndex(Math.round(track.scrollLeft / track.clientWidth));
        }}
        className="flex snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {files.map((path) => (
          <div key={path} className="w-full shrink-0 snap-center">
            <MediaSlide path={path} frame={FRAME_BY_FORMAT[format]} />
          </div>
        ))}
      </div>

      {files.length > 1 ? (
        <>
          <span className="absolute top-2.5 right-2.5 rounded-full bg-black/60 px-2 py-0.5 text-[11px] text-white tabular-nums">
            {index + 1}/{files.length}
          </span>
          {index > 0 ? (
            <SlideButton
              side="left"
              label={translate("resources.content_pieces.versions.previous")}
              onClick={() => goTo(index - 1)}
            />
          ) : null}
          {index < files.length - 1 ? (
            <SlideButton
              side="right"
              label={translate("resources.content_pieces.versions.next")}
              onClick={() => goTo(index + 1)}
            />
          ) : null}
          <div className="absolute inset-x-0 bottom-2.5 flex justify-center gap-1.5">
            {files.map((path, dot) => (
              <span
                key={path}
                className={`size-1.5 rounded-full ${
                  dot === index ? "bg-white" : "bg-white/40"
                }`}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

const SlideButton = ({
  side,
  label,
  onClick,
}: {
  side: "left" | "right";
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className={`absolute top-1/2 hidden size-8 -translate-y-1/2 place-items-center rounded-full bg-white/85 text-black shadow md:grid ${
      side === "left" ? "left-2.5" : "right-2.5"
    }`}
  >
    {side === "left" ? (
      <ChevronLeft className="size-4" />
    ) : (
      <ChevronRight className="size-4" />
    )}
  </button>
);

const MediaSlide = ({ path, frame }: { path: string; frame: string }) => {
  const translate = useTranslate();
  const url = useSignedUrl(path);

  if (isImagePath(path) || isVideoPath(path)) {
    return (
      <div className={`mx-auto w-full ${frame} grid place-items-center`}>
        {!url ? null : isVideoPath(path) ? (
          <video
            src={url}
            controls
            playsInline
            preload="metadata"
            className="h-full w-full object-contain"
          />
        ) : (
          <img
            src={url}
            alt=""
            className="h-full w-full object-contain"
            draggable={false}
          />
        )}
      </div>
    );
  }

  return (
    <a
      href={url ?? undefined}
      target="_blank"
      rel="noreferrer"
      className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-white/80 no-underline hover:text-white"
    >
      <FileText className="size-6" />
      {translate("resources.content_pieces.versions.open")}
    </a>
  );
};

const Caption = ({ text, name }: { text: string; name?: string }) => {
  const translate = useTranslate();
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > 140 || text.split("\n").length > 3;

  return (
    <div className="text-sm leading-relaxed">
      <p
        className={`whitespace-pre-line ${
          expanded || !isLong ? "" : "line-clamp-3"
        }`}
      >
        {name ? <span className="mr-1.5 font-semibold">{name}</span> : null}
        {text}
      </p>
      {isLong ? (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-0.5 text-xs text-muted-foreground"
        >
          {translate(
            expanded
              ? "resources.content_pieces.versions.less"
              : "resources.content_pieces.versions.more",
          )}
        </button>
      ) : null}
    </div>
  );
};

// Older versions: a compact row that opens the first file or the link.
const VersionRow = ({ version }: { version: ContentPieceVersion }) => {
  const translate = useTranslate();
  const signedUrl = useSignedUrl(version.file_path);
  const href = version.external_url ?? signedUrl ?? undefined;
  const fileCount = versionFiles(version).length;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-3 rounded-xl bg-secondary px-3 py-2.5 text-sm no-underline hover:bg-accent"
    >
      {version.external_url ? (
        <ExternalLink className="size-4 shrink-0 text-primary" />
      ) : (
        <FileText className="size-4 shrink-0 text-primary" />
      )}
      <span className="min-w-0 flex-1">
        <span className="font-medium">
          {translate("resources.content_pieces.versions.version", {
            number: version.version_number,
          })}
          {fileCount > 1 ? ` · ${fileCount}` : ""}
        </span>
        {version.note ? (
          <span className="block truncate text-xs text-muted-foreground">
            {version.note}
          </span>
        ) : null}
      </span>
      <span className="text-xs text-primary">
        {translate("resources.content_pieces.versions.open")}
      </span>
    </a>
  );
};

const AddVersionForm = ({
  piece,
  nextNumber,
  onDone,
}: {
  piece: ContentPiece;
  nextNumber: number;
  onDone: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [create] = useCreate<ContentPieceVersion>();
  const [files, setFiles] = useState<File[]>([]);
  const [link, setLink] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (files.length === 0 && !link.trim()) {
      setError(
        translate("resources.content_pieces.versions.need_file_or_link"),
      );
      return;
    }
    if (files.some((file) => file.size > MAX_UPLOAD_BYTES)) {
      setError(translate("resources.content_pieces.versions.too_large"));
      return;
    }
    setIsSaving(true);
    // Files are uploaded in the chosen order: that is the carousel order.
    const paths: string[] = [];
    for (const [position, file] of files.entries()) {
      const safeName = file.name.replace(/[^\w.-]+/g, "_");
      const path = `${piece.company_id}/${piece.id}/${Date.now()}-${position}-${safeName}`;
      const { error: uploadError } = await getSupabaseClient()
        .storage.from(CONTENT_BUCKET)
        .upload(path, file, { contentType: file.type });
      if (uploadError) {
        setIsSaving(false);
        notify("ra.notification.http_error", { type: "error" });
        return;
      }
      paths.push(path);
    }
    const [firstPath = null, ...extraPaths] = paths;
    create(
      "content_piece_versions",
      {
        data: {
          piece_id: piece.id,
          version_number: nextNumber,
          file_path: firstPath,
          extra_file_paths: extraPaths,
          external_url: link.trim() || null,
          note: note.trim() || null,
        },
      },
      {
        onSuccess: () => {
          setIsSaving(false);
          onDone();
        },
        onError: () => {
          setIsSaving(false);
          notify("ra.notification.http_error", { type: "error" });
        },
      },
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-2.5 rounded-2xl border border-border p-3"
    >
      <label className="text-xs text-muted-foreground">
        {translate("resources.content_pieces.versions.upload")}
        <Input
          type="file"
          multiple
          accept="image/*,video/*,application/pdf"
          className="mt-1"
          onChange={(event) => {
            setFiles(Array.from(event.target.files ?? []));
            setError(null);
          }}
        />
      </label>
      <p className="text-[11px] text-muted-foreground">
        {translate("resources.content_pieces.versions.upload_hint")}
      </p>
      <Input
        placeholder={translate("resources.content_pieces.versions.link")}
        value={link}
        onChange={(event) => {
          setLink(event.target.value);
          setError(null);
        }}
      />
      <Input
        placeholder={translate("resources.content_pieces.versions.note")}
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          {translate("ra.action.cancel")}
        </Button>
        <Button
          type="submit"
          size="sm"
          className="rounded-full"
          disabled={isSaving}
        >
          {translate(
            isSaving
              ? "resources.content_pieces.versions.uploading"
              : "resources.content_pieces.versions.save",
          )}
        </Button>
      </div>
    </form>
  );
};
